// @docs:
//   - docs/wip/CREW_PAPER_REQUESTS.md  § API contract (skipper), § Decisions (defaults)
// ==============================================
// The skipper's "Request crew papers" form (Skipper-Request artboard) as a
// plain model: defaults, the papers × crew tick grid, validation, and the
// RequestInput the API takes. The page is a thin view over this.
//
// Defaults (Tony, 2026-09-25): due a week before departure (never before
// today), remind every 3 days and daily for the last 3, a daily status email,
// suggested access 30 days after arrival. The passport date defaults to three
// months after arrival (NZ entry rule of thumb); the skipper can change it.
//
// Phase 3: the time zone the emails follow (default the browser's; a saved
// request keeps its own), dates that look like a typo (more than a year
// away, or in the past) are warned about without blocking, and an OPEN
// request can be edited too — dates, reminders, status, zone; adding or
// removing people — but never what someone already asked is asked for.
// ==============================================

import type { RequestDetail, RequestInput, RequestSet, RecipientInput, StatusCadence } from './types';
import { addDays, addMonths, daysBetween, isDate } from './dates';

export type AccessChoice = 'on_arrival' | 'plus30' | 'plus90';
export type CadenceChoice = 'each_upload' | 'daily' | 'weekly' | 'every_3_days';
export const REMIND_CHOICES = [2, 3, 5, 7] as const;
export const MAX_CREW = 30;
export const MAX_PAPERS_PER_PERSON = 20;

export interface CrewRow { key: string; email: string; name: string; source: 'aboard' | 'new' | 'saved'; note?: string; recipientId?: string }
export interface PaperRow { type: string; label: string; required: boolean }

export interface RequestForm {
  from: string; to: string; departOn: string; arriveOn: string;
  dueOn: string; dueTouched: boolean;
  passportValidUntil: string; passportTouched: boolean;
  papers: PaperRow[];
  crew: CrewRow[];
  /** crew key → the paper types asked of that person */
  ticks: Record<string, string[]>;
  remindEveryDays: number; dailyFinal: boolean;
  cadence: CadenceChoice; statusWeekday: number;
  access: AccessChoice;
  /** IANA zone the request's emails go out in (08:00 there) */
  timezone: string;
}

let seq = 0;
const newKey = () => `c${++seq}`;
const norm = (email: string) => email.trim().toLowerCase();
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

export function defaultDueOn(departOn: string, today: string): string {
  if (!isDate(departOn)) return '';
  const d = addDays(departOn, -7);
  return d < today ? today : d;
}

export function defaultPassportValidUntil(arriveOn: string): string {
  return isDate(arriveOn) ? addMonths(arriveOn, 3) : '';
}

export function emptyForm(set: RequestSet | null, timezone = 'UTC'): RequestForm {
  return {
    from: '', to: '', departOn: '', arriveOn: '', dueOn: '', dueTouched: false, passportValidUntil: '', passportTouched: false,
    papers: (set?.items ?? []).map((i) => ({ type: i.type, label: i.label, required: i.required })),
    crew: [], ticks: {},
    remindEveryDays: 3, dailyFinal: true, cadence: 'daily', statusWeekday: 1, access: 'plus30', timezone,
  };
}

const DATE_LABEL = { departOn: 'Departure', arriveOn: 'Arrival', dueOn: 'The due date' } as const;

/** Dates that look like a slip of the year: in the past, or more than a year away. Warnings only: they never block. */
export function dateWarnings(f: Pick<RequestForm, 'departOn' | 'arriveOn' | 'dueOn'>, today: string): Partial<Record<keyof typeof DATE_LABEL, string>> {
  const out: Partial<Record<keyof typeof DATE_LABEL, string>> = {};
  for (const k of Object.keys(DATE_LABEL) as Array<keyof typeof DATE_LABEL>) {
    const v = f[k];
    if (!isDate(v)) continue;
    const days = daysBetween(today, v);
    if (days < 0) out[k] = `${DATE_LABEL[k]} is in the past — is the date right?`;
    else if (days > 365) {
      const years = Math.floor(days / 365.25);
      out[k] = `${DATE_LABEL[k]} is ${years >= 2 ? `${years} years` : 'more than a year'} away — is the year right?`;
    }
  }
  return out;
}

/** Change the passage dates; untouched defaults (due date, passport date) follow them. */
export function withDates(f: RequestForm, patch: { departOn?: string; arriveOn?: string }, today: string): RequestForm {
  const next = { ...f, ...patch };
  if (!next.dueTouched) next.dueOn = defaultDueOn(next.departOn, today);
  if (!next.passportTouched) next.passportValidUntil = defaultPassportValidUntil(next.arriveOn);
  return next;
}

export function addCrew(f: RequestForm, row: Omit<CrewRow, 'key'>): RequestForm {
  const key = newKey();
  return { ...f, crew: [...f.crew, { ...row, key }], ticks: { ...f.ticks, [key]: f.papers.filter((p) => p.required).map((p) => p.type) } };
}

export function removeCrew(f: RequestForm, key: string): RequestForm {
  const ticks = { ...f.ticks }; delete ticks[key];
  return { ...f, crew: f.crew.filter((c) => c.key !== key), ticks };
}

/**
 * A paper added from the catalog is asked of everyone; untick anyone who does not need it.
 * On a sent request (`sent`), only of people not yet asked: what someone was asked for is fixed.
 */
export function addPaper(f: RequestForm, p: PaperRow, opts: { sent?: boolean } = {}): RequestForm {
  if (f.papers.some((x) => x.type === p.type)) return f;
  const ticks: Record<string, string[]> = {};
  for (const c of f.crew) ticks[c.key] = opts.sent && c.recipientId ? [...(f.ticks[c.key] ?? [])] : [...(f.ticks[c.key] ?? []), p.type];
  return { ...f, papers: [...f.papers, p], ticks };
}

export function removePaper(f: RequestForm, type: string): RequestForm {
  const ticks: Record<string, string[]> = {};
  for (const [k, v] of Object.entries(f.ticks)) ticks[k] = v.filter((t) => t !== type);
  return { ...f, papers: f.papers.filter((p) => p.type !== type), ticks };
}

export function toggleTick(f: RequestForm, crewKey: string, type: string): RequestForm {
  const cur = f.ticks[crewKey] ?? [];
  const next = cur.includes(type) ? cur.filter((t) => t !== type) : [...cur, type];
  // keep the grid's paper order
  return { ...f, ticks: { ...f.ticks, [crewKey]: f.papers.map((p) => p.type).filter((t) => next.includes(t)) } };
}

export function toggleRequired(f: RequestForm, type: string): RequestForm {
  return { ...f, papers: f.papers.map((p) => (p.type === type ? { ...p, required: !p.required } : p)) };
}

export const crewLabel = (c: CrewRow) => c.name.trim() || c.email.trim() || 'this person';
const asksForPassport = (f: RequestForm) => f.crew.some((c) => (f.ticks[c.key] ?? []).includes('passport'));

export type FormErrors = Record<string, string>;

/**
 * Field → message. 'draft' checks only what would be wrong in any case; 'send' also needs
 * everything filled; 'edit' (a sent request) is 'send' without the passed-due-date rule, and
 * asks papers only of people added now.
 */
export function validateForm(f: RequestForm, mode: 'draft' | 'send' | 'edit', ctx: { today: string; myEmail: string | null | undefined }): FormErrors {
  const e: FormErrors = {};
  const send = mode === 'send' || mode === 'edit';
  if (send && !f.from.trim()) e.from = 'Where are you sailing from?';
  if (send && !f.to.trim()) e.to = 'Where are you sailing to?';
  if (send && !isDate(f.departOn)) e.departOn = 'Choose the departure date';
  if (send && !isDate(f.arriveOn)) e.arriveOn = 'Choose the arrival date';
  if (isDate(f.departOn) && isDate(f.arriveOn) && f.arriveOn < f.departOn) e.arriveOn = 'Arrival must be on or after departure';
  if (send && !isDate(f.dueOn)) e.dueOn = 'Choose when the papers are due';
  else if (isDate(f.dueOn) && isDate(f.arriveOn) && f.dueOn > f.arriveOn) e.dueOn = 'Papers must be due before arrival';
  else if (mode === 'send' && isDate(f.dueOn) && f.dueOn < ctx.today) e.dueOn = 'The due date has passed — choose a later one';

  if (send && !f.crew.length) e.crew = 'Add at least one crew member';
  if (f.crew.length > MAX_CREW) e.crew = `Ask up to ${MAX_CREW} people at a time`;
  const seen = new Set<string>();
  const me = ctx.myEmail ? norm(ctx.myEmail) : null;
  for (const c of f.crew) {
    const email = norm(c.email);
    const k = `crew:${c.key}`;
    if (!EMAIL_RE.test(email)) e[k] = 'Enter a valid email address';
    else if (seen.has(email)) e[k] = 'This person is on the list twice';
    else if (me && email === me) e[k] = 'You can’t ask yourself for papers';
    else if ((f.ticks[c.key] ?? []).length > MAX_PAPERS_PER_PERSON) e[k] = `Ask for up to ${MAX_PAPERS_PER_PERSON} papers per person`;
    else if (send && !(mode === 'edit' && c.recipientId) && !(f.ticks[c.key] ?? []).length) e[k] = `Choose at least one paper for ${crewLabel(c)}`;
    seen.add(email);
  }
  return e;
}

const text = (v: string) => v.trim() || null;
const date = (v: string) => (isDate(v) ? v : null);

function itemsFor(f: RequestForm, key: string) {
  const mine = f.ticks[key] ?? [];
  return f.papers.filter((p) => mine.includes(p.type)).map((p) => ({ type: p.type, required: p.required }));
}

/** The request fields without recipients — what PATCH takes. */
export function requestFields(f: RequestForm): Omit<RequestInput, 'recipients' | 'set'> {
  const cadence: StatusCadence = f.cadence === 'every_3_days' ? 'every_n_days' : f.cadence;
  return {
    from: text(f.from), to: text(f.to), departOn: date(f.departOn), arriveOn: date(f.arriveOn), dueOn: date(f.dueOn),
    remindEveryDays: f.remindEveryDays, dailyFinalDays: f.dailyFinal ? 3 : 0,
    statusCadence: cadence, statusWeekday: cadence === 'weekly' ? f.statusWeekday : null, statusEveryDays: cadence === 'every_n_days' ? 3 : null,
    suggestedAccess: f.access === 'on_arrival' ? 'on_arrival' : 'arrival_plus_days', suggestedAccessDays: f.access === 'plus30' ? 30 : f.access === 'plus90' ? 90 : null,
    passportValidUntil: asksForPassport(f) ? date(f.passportValidUntil) : null,
    timezone: f.timezone,
  };
}

export function toRequestInput(f: RequestForm): RequestInput {
  return {
    ...requestFields(f),
    // Items are always explicit, never a `set`: the API fills a set's items in for
    // anyone sent with none, which would undo a skipper's unticking.
    recipients: f.crew.map((c) => ({ email: norm(c.email), name: text(c.name), items: itemsFor(f, c.key) })),
  };
}

/** A saved draft back in the form (the page's edit mode). */
export function formFromDetail(d: RequestDetail, set: RequestSet | null): RequestForm {
  const base = emptyForm(set);
  const papers = [...base.papers];
  for (const r of d.recipients) for (const i of r.items) {
    const at = papers.findIndex((p) => p.type === i.type);
    if (at === -1) papers.push({ type: i.type, label: i.label, required: i.required });
    else papers[at] = { ...papers[at], required: i.required };
  }
  const crew: CrewRow[] = []; const ticks: Record<string, string[]> = {};
  for (const r of d.recipients) {
    const key = newKey();
    crew.push({ key, email: r.email, name: r.name ?? '', source: 'saved', recipientId: r.id });
    ticks[key] = papers.map((p) => p.type).filter((t) => r.items.some((i) => i.type === t));
  }
  const cadence: CadenceChoice = d.statusCadence === 'every_n_days' ? 'every_3_days' : d.statusCadence;
  return {
    ...base, papers, crew, ticks,
    from: d.fromPlace ?? '', to: d.toPlace ?? '', departOn: d.departOn ?? '', arriveOn: d.arriveOn ?? '',
    dueOn: d.dueOn ?? '', dueTouched: !!d.dueOn, passportValidUntil: d.passportValidUntil ?? '', passportTouched: !!d.passportValidUntil,
    remindEveryDays: d.remindEveryDays, dailyFinal: d.dailyFinalDays > 0, cadence, statusWeekday: d.statusWeekday ?? 1,
    access: d.suggestedAccess === 'on_arrival' ? 'on_arrival' : (d.suggestedAccessDays ?? 30) >= 60 ? 'plus90' : 'plus30',
    timezone: d.timezone || base.timezone,
  };
}

const sameItems = (a: Array<{ type: string; required: boolean }>, b: Array<{ type: string; required: boolean }>) =>
  a.length === b.length && a.every((x) => b.some((y) => y.type === x.type && y.required === x.required));

/** Who to add, change and remove to bring a saved draft in line with the form. */
export function recipientChanges(d: RequestDetail, f: RequestForm): {
  add: RecipientInput[]; update: Array<{ id: string; name: string | null; items: Array<{ type: string; required: boolean }> }>; remove: string[];
} {
  const inForm = new Map(f.crew.map((c) => [norm(c.email), c]));
  const saved = new Map(d.recipients.map((r) => [norm(r.email), r]));
  const remove = d.recipients.filter((r) => !inForm.has(norm(r.email))).map((r) => r.id);
  const add: RecipientInput[] = []; const update: Array<{ id: string; name: string | null; items: Array<{ type: string; required: boolean }> }> = [];
  for (const c of f.crew) {
    const items = itemsFor(f, c.key);
    const r = saved.get(norm(c.email));
    if (!r) { add.push({ email: norm(c.email), name: text(c.name), items }); continue; }
    const was = r.items.map((i) => ({ type: i.type, required: i.required }));
    if ((r.name ?? null) !== text(c.name) || !sameItems(was, items)) update.push({ id: r.id, name: text(c.name), items });
  }
  return { add, update, remove };
}

export interface RequestWriter {
  create(vid: string, input: RequestInput): Promise<RequestDetail>;
  update(vid: string, rid: string, input: Omit<RequestInput, 'recipients' | 'set'>): Promise<RequestDetail>;
  addRecipient(vid: string, rid: string, input: RecipientInput): Promise<RequestDetail>;
  updateRecipient(vid: string, rid: string, recId: string, input: { name?: string | null; items?: Array<{ type: string; required: boolean }> }): Promise<RequestDetail>;
  removeRecipient(vid: string, rid: string, recId: string): Promise<RequestDetail>;
  get(vid: string, rid: string): Promise<RequestDetail>;
}

/**
 * Create the draft, or bring a saved one in line with the form. Returns the saved request.
 * `sent`: an open request. People are added (emailed at once) and removed (what they shared
 * is withdrawn) and names change, but nobody's papers do: the API allows that only in a draft.
 */
export async function saveRequest(w: RequestWriter, vid: string, existing: RequestDetail | null, f: RequestForm, opts: { sent?: boolean } = {}): Promise<RequestDetail> {
  if (!existing) return w.create(vid, toRequestInput(f));
  await w.update(vid, existing.id, requestFields(f));
  const ch = recipientChanges(existing, f);
  for (const id of ch.remove) await w.removeRecipient(vid, existing.id, id);
  for (const u of ch.update) {
    if (!opts.sent) { await w.updateRecipient(vid, existing.id, u.id, { name: u.name, items: u.items }); continue; }
    const was = existing.recipients.find((r) => r.id === u.id);
    if ((was?.name ?? null) !== u.name) await w.updateRecipient(vid, existing.id, u.id, { name: u.name });
  }
  for (const a of ch.add) await w.addRecipient(vid, existing.id, a);
  return w.get(vid, existing.id);
}

/** Where an API validation error belongs on the form. */
export function serverErrorFields(err: { code?: string | null; message: string; field?: string; fields?: string[]; recipientIds?: string[] }, f: RequestForm): FormErrors {
  const m = err.message;
  switch (err.code) {
    case 'MISSING_DATES': return Object.fromEntries((err.fields ?? ['departOn', 'arriveOn', 'dueOn']).map((k) => [k, m]));
    case 'INVALID_DATE': return { [err.field && ['departOn', 'arriveOn', 'dueOn', 'passportValidUntil'].includes(err.field) ? err.field : 'form']: m };
    case 'INVALID_DATES': return { [/due/i.test(m) ? 'dueOn' : 'arriveOn']: m };
    case 'NO_ITEMS': {
      const keys = f.crew.filter((c) => c.recipientId && err.recipientIds?.includes(c.recipientId)).map((c) => `crew:${c.key}`);
      return keys.length ? Object.fromEntries(keys.map((k) => [k, m])) : { crew: m };
    }
    case 'NO_RECIPIENTS': case 'INVALID_EMAIL': case 'SELF_REQUEST': case 'DUPLICATE_RECIPIENT': case 'TOO_MANY_RECIPIENTS':
    case 'TOO_MANY_ITEMS': case 'INVALID_TYPE': case 'DUPLICATE_TYPE': return { crew: m };
    case 'INVALID_REMINDERS': return { reminders: m };
    case 'INVALID_CADENCE': return { status: m };
    case 'INVALID_ACCESS': return { access: m };
    case 'INVALID_TIMEZONE': return { timezone: m };
    default: return { form: m };
  }
}
